/**
 * AdminDocumentVerification - Admin UI for document verification queue
 * Allows admins to view, approve, and reject advertiser verification documents
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { 
  FileCheck, Clock, CheckCircle, XCircle, AlertTriangle,
  ChevronLeft, ChevronRight, Loader2, Search, Eye, Download,
  User, Building, Calendar, RefreshCw, Filter, Mail, Send
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDocumentVerification = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [queue, setQueue] = useState([]);
  const [expiringDocs, setExpiringDocs] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [requirements, setRequirements] = useState(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('pending');
  const [documentTypeFilter, setDocumentTypeFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;
  
  // Modal state
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewAction, setReviewAction] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Email preview state
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [emailPreviewType, setEmailPreviewType] = useState('approved');
  const [emailPreviewHtml, setEmailPreviewHtml] = useState('');
  const [emailPreviewSubject, setEmailPreviewSubject] = useState('');
  const [emailPreviewLoading, setEmailPreviewLoading] = useState(false);
  const [previewParams, setPreviewParams] = useState({
    advertiser_name: 'John Smith',
    document_type: 'government_id',
    verification_level: 1,
    rejection_reason: 'document_illegible',
    days_until_expiry: 7
  });

  const getAuthHeader = () => {
    const token = localStorage.getItem('admin_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/verification/admin/queue/stats`);
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  const fetchQueue = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        status: statusFilter,
        skip: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage
      });
      if (documentTypeFilter !== 'all') {
        params.append('document_type', documentTypeFilter);
      }
      
      const response = await axios.get(`${API_URL}/api/verification/admin/queue?${params}`);
      setQueue(response.data.queue || []);
      setTotalItems(response.data.total || 0);
    } catch (err) {
      console.error('Error fetching queue:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, documentTypeFilter, currentPage]);

  const fetchExpiringDocs = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/verification/admin/expiring-soon?days_ahead=30`);
      setExpiringDocs(response.data.expiring_documents || []);
    } catch (err) {
      console.error('Error fetching expiring docs:', err);
    }
  }, []);

  const fetchAuditLog = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/verification/admin/audit-log?limit=20`);
      setAuditLog(response.data.logs || []);
    } catch (err) {
      console.error('Error fetching audit log:', err);
    }
  }, []);

  const fetchRequirements = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/verification/requirements`);
      setRequirements(response.data);
    } catch (err) {
      console.error('Error fetching requirements:', err);
    }
  }, []);

  const fetchEmailPreview = useCallback(async (templateType, params) => {
    setEmailPreviewLoading(true);
    try {
      const queryParams = new URLSearchParams({
        advertiser_name: params.advertiser_name,
        document_type: params.document_type,
        verification_level: params.verification_level,
        rejection_reason: params.rejection_reason,
        days_until_expiry: params.days_until_expiry
      });
      
      const response = await axios.get(
        `${API_URL}/api/verification/admin/email-preview/${templateType}?${queryParams}`
      );
      
      setEmailPreviewHtml(response.data.html);
      setEmailPreviewSubject(response.data.subject);
    } catch (err) {
      console.error('Error fetching email preview:', err);
      setEmailPreviewHtml('<p>Error loading preview</p>');
    } finally {
      setEmailPreviewLoading(false);
    }
  }, []);

  // Load email preview when type or params change
  useEffect(() => {
    if (showEmailPreview) {
      fetchEmailPreview(emailPreviewType, previewParams);
    }
  }, [emailPreviewType, previewParams, showEmailPreview, fetchEmailPreview]);

  useEffect(() => {
    fetchStats();
    fetchRequirements();
  }, [fetchStats, fetchRequirements]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  const handleReviewDocument = (doc, action) => {
    setSelectedDocument(doc);
    setReviewAction(action);
    setRejectionReason('');
    setAdminNotes('');
    setShowReviewModal(true);
  };

  const handleSubmitReview = async () => {
    if (!selectedDocument) return;
    
    setSubmitting(true);
    try {
      const params = new URLSearchParams({ action: reviewAction });
      if (rejectionReason) params.append('rejection_reason', rejectionReason);
      if (adminNotes) params.append('admin_notes', adminNotes);
      
      await axios.post(
        `${API_URL}/api/verification/admin/verify/${selectedDocument.advertiser_id}/${selectedDocument.document.name}?${params}`
      );
      
      setShowReviewModal(false);
      fetchQueue();
      fetchStats();
      fetchAuditLog();
    } catch (err) {
      console.error('Error submitting review:', err);
      alert('Failed to submit review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCheckExpiries = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/verification/admin/check-expiries`);
      alert(`Processed ${response.data.expired_documents_processed} expired documents`);
      fetchQueue();
      fetchStats();
    } catch (err) {
      console.error('Error checking expiries:', err);
    }
  };

  const getDocumentTypeBadge = (type) => {
    const colors = {
      government_id: 'bg-blue-100 text-blue-800',
      business_registration: 'bg-purple-100 text-purple-800',
      registered_address: 'bg-green-100 text-green-800',
      selfie: 'bg-orange-100 text-orange-800'
    };
    const labels = {
      government_id: 'Gov ID',
      business_registration: 'Business Reg',
      registered_address: 'Address Proof',
      selfie: 'Selfie'
    };
    return (
      <Badge className={colors[type] || 'bg-gray-100 text-gray-800'}>
        {labels[type] || type}
      </Badge>
    );
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      verified: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      rejected: { color: 'bg-red-100 text-red-800', icon: XCircle },
      expired: { color: 'bg-gray-100 text-gray-800', icon: AlertTriangle }
    };
    const { color, icon: Icon } = config[status] || config.pending;
    return (
      <Badge className={`${color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {status}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="admin-document-verification">
      {/* Header */}
      <div className="mb-6">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/admin')}
          className="mb-2"
        >
          <ChevronLeft className="h-4 w-4 mr-1" /> Back to Admin
        </Button>
        <h1 className="text-2xl font-bold text-gray-900">Document Verification</h1>
        <p className="text-gray-600">Review and manage advertiser verification documents</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending Review</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {stats?.status_counts?.pending || 0}
                </p>
              </div>
              <Clock className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Verified</p>
                <p className="text-2xl font-bold text-green-600">
                  {stats?.status_counts?.verified || 0}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Rejected</p>
                <p className="text-2xl font-bold text-red-600">
                  {stats?.status_counts?.rejected || 0}
                </p>
              </div>
              <XCircle className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Avg Wait Time</p>
                <p className="text-2xl font-bold text-blue-600">
                  {stats?.average_wait_hours || 0}h
                </p>
              </div>
              <Calendar className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="queue" className="space-y-4">
        <TabsList>
          <TabsTrigger value="queue" data-testid="tab-queue">
            <FileCheck className="h-4 w-4 mr-2" />
            Verification Queue
          </TabsTrigger>
          <TabsTrigger value="expiring" data-testid="tab-expiring">
            <AlertTriangle className="h-4 w-4 mr-2" />
            Expiring Soon ({expiringDocs.length})
          </TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">
            <Clock className="h-4 w-4 mr-2" />
            Audit Log
          </TabsTrigger>
          <TabsTrigger value="email-preview" data-testid="tab-email-preview" onClick={() => setShowEmailPreview(true)}>
            <Mail className="h-4 w-4 mr-2" />
            Email Preview
          </TabsTrigger>
        </TabsList>

        {/* Verification Queue Tab */}
        <TabsContent value="queue">
          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <CardTitle className="flex items-center gap-2">
                  <FileCheck className="h-5 w-5" />
                  Document Queue
                </CardTitle>
                <div className="flex flex-wrap items-center gap-2">
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="verified">Verified</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                      <SelectItem value="expired">Expired</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={documentTypeFilter} onValueChange={setDocumentTypeFilter}>
                    <SelectTrigger className="w-[160px]">
                      <SelectValue placeholder="Document Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="government_id">Government ID</SelectItem>
                      <SelectItem value="business_registration">Business Reg</SelectItem>
                      <SelectItem value="registered_address">Address Proof</SelectItem>
                      <SelectItem value="selfie">Selfie</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => { fetchQueue(); fetchStats(); }}
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                </div>
              ) : queue.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileCheck className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No documents in queue</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {queue.map((item, index) => (
                    <div 
                      key={`${item.advertiser_id}-${item.document?.name}-${index}`}
                      className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                      data-testid={`queue-item-${index}`}
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            {item.advertiser_type === 'agent' ? (
                              <Building className="h-4 w-4 text-purple-500" />
                            ) : (
                              <User className="h-4 w-4 text-blue-500" />
                            )}
                            <span className="font-medium">{item.advertiser_name}</span>
                            <Badge variant="outline" className="text-xs">
                              {item.advertiser_type}
                            </Badge>
                          </div>
                          <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                            {getDocumentTypeBadge(item.document?.name)}
                            {getStatusBadge(item.document?.status)}
                            <span className="text-gray-400">|</span>
                            <span>Submitted: {formatDate(item.document?.submitted_at)}</span>
                          </div>
                          <p className="text-sm text-gray-500 mt-1">{item.advertiser_email}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {item.document?.document_url && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(item.document.document_url, '_blank')}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              View
                            </Button>
                          )}
                          {item.document?.status === 'pending' && (
                            <>
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => handleReviewDocument(item, 'approve')}
                                data-testid={`approve-btn-${index}`}
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleReviewDocument(item, 'reject')}
                                data-testid={`reject-btn-${index}`}
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                Reject
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <span className="text-sm text-gray-500">
                    Showing {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(p => p - 1)}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm">Page {currentPage} of {totalPages}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(p => p + 1)}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Expiring Documents Tab */}
        <TabsContent value="expiring">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-orange-500" />
                  Documents Expiring Soon
                </CardTitle>
                <Button variant="outline" onClick={handleCheckExpiries}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Process Expiries
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Button 
                variant="ghost" 
                className="mb-4"
                onClick={fetchExpiringDocs}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              
              {expiringDocs.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle className="h-12 w-12 mx-auto mb-2 text-green-500 opacity-50" />
                  <p>No documents expiring in the next 30 days</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {expiringDocs.map((doc, index) => (
                    <div 
                      key={`${doc.advertiser_id}-${doc.document_type}-${index}`}
                      className="border rounded-lg p-4 bg-orange-50"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{doc.advertiser_name}</p>
                          <p className="text-sm text-gray-600">{doc.advertiser_email}</p>
                          <div className="flex items-center gap-2 mt-1">
                            {getDocumentTypeBadge(doc.document_type)}
                            <span className="text-sm text-orange-600">
                              Expires: {formatDate(doc.expires_at)}
                            </span>
                          </div>
                        </div>
                        <Badge className="bg-orange-100 text-orange-800">
                          {Math.ceil(doc.days_until_expiry)} days left
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Log Tab */}
        <TabsContent value="audit">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Verification Audit Log
                </CardTitle>
                <Button variant="ghost" onClick={fetchAuditLog}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {auditLog.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Clock className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No audit log entries</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {auditLog.map((log, index) => (
                    <div 
                      key={index}
                      className="flex items-center gap-4 p-3 border-b last:border-b-0"
                    >
                      <div className={`p-2 rounded-full ${log.action === 'approve' ? 'bg-green-100' : 'bg-red-100'}`}>
                        {log.action === 'approve' ? (
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-600" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm">
                          <span className="font-medium">{log.document_type}</span>
                          {' '}{log.action === 'approve' ? 'approved' : 'rejected'}
                          {' '}for advertiser {log.advertiser_id?.slice(-6)}
                        </p>
                        {log.rejection_reason && (
                          <p className="text-xs text-red-600">Reason: {log.rejection_reason}</p>
                        )}
                        {log.admin_notes && (
                          <p className="text-xs text-gray-500">Notes: {log.admin_notes}</p>
                        )}
                      </div>
                      <span className="text-xs text-gray-400">
                        {formatDate(log.created_at)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Preview Tab */}
        <TabsContent value="email-preview">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Email Template Preview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Controls Panel */}
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium block mb-2">Email Template</label>
                    <Select value={emailPreviewType} onValueChange={setEmailPreviewType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="approved">
                          <span className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            Document Approved
                          </span>
                        </SelectItem>
                        <SelectItem value="rejected">
                          <span className="flex items-center gap-2">
                            <XCircle className="h-4 w-4 text-red-500" />
                            Document Rejected
                          </span>
                        </SelectItem>
                        <SelectItem value="expiring">
                          <span className="flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4 text-orange-500" />
                            Document Expiring
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-sm font-medium block mb-2">Advertiser Name</label>
                    <Input
                      value={previewParams.advertiser_name}
                      onChange={(e) => setPreviewParams(p => ({ ...p, advertiser_name: e.target.value }))}
                      placeholder="John Smith"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium block mb-2">Document Type</label>
                    <Select 
                      value={previewParams.document_type} 
                      onValueChange={(v) => setPreviewParams(p => ({ ...p, document_type: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="government_id">Government ID</SelectItem>
                        <SelectItem value="business_registration">Business Registration</SelectItem>
                        <SelectItem value="registered_address">Address Proof</SelectItem>
                        <SelectItem value="selfie">Selfie Verification</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {emailPreviewType === 'approved' && (
                    <div>
                      <label className="text-sm font-medium block mb-2">Verification Level</label>
                      <Select 
                        value={String(previewParams.verification_level)} 
                        onValueChange={(v) => setPreviewParams(p => ({ ...p, verification_level: parseInt(v) }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">Level 1 - Identity Verified</SelectItem>
                          <SelectItem value="2">Level 2 - Business Verified</SelectItem>
                          <SelectItem value="3">Level 3 - Performance Verified</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {emailPreviewType === 'rejected' && (
                    <div>
                      <label className="text-sm font-medium block mb-2">Rejection Reason</label>
                      <Select 
                        value={previewParams.rejection_reason} 
                        onValueChange={(v) => setPreviewParams(p => ({ ...p, rejection_reason: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="document_illegible">Document not legible</SelectItem>
                          <SelectItem value="name_mismatch">Name doesn't match</SelectItem>
                          <SelectItem value="document_expired">Document has expired</SelectItem>
                          <SelectItem value="wrong_document_type">Wrong document type</SelectItem>
                          <SelectItem value="suspicious_document">Suspicious document</SelectItem>
                          <SelectItem value="incomplete_information">Incomplete information</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {emailPreviewType === 'expiring' && (
                    <div>
                      <label className="text-sm font-medium block mb-2">Days Until Expiry</label>
                      <Select 
                        value={String(previewParams.days_until_expiry)} 
                        onValueChange={(v) => setPreviewParams(p => ({ ...p, days_until_expiry: parseInt(v) }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="30">30 days</SelectItem>
                          <SelectItem value="14">14 days</SelectItem>
                          <SelectItem value="7">7 days</SelectItem>
                          <SelectItem value="3">3 days</SelectItem>
                          <SelectItem value="1">1 day</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <Button 
                    onClick={() => fetchEmailPreview(emailPreviewType, previewParams)}
                    className="w-full"
                    disabled={emailPreviewLoading}
                  >
                    {emailPreviewLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    Refresh Preview
                  </Button>
                </div>

                {/* Preview Panel */}
                <div className="lg:col-span-2">
                  <div className="border rounded-lg overflow-hidden">
                    {/* Subject Line */}
                    <div className="bg-gray-100 px-4 py-3 border-b">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="font-medium text-gray-500">Subject:</span>
                        <span className="text-gray-800">{emailPreviewSubject || 'Loading...'}</span>
                      </div>
                    </div>
                    
                    {/* Email Body */}
                    <div className="bg-white">
                      {emailPreviewLoading ? (
                        <div className="flex items-center justify-center py-20">
                          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                        </div>
                      ) : (
                        <iframe
                          srcDoc={emailPreviewHtml}
                          className="w-full h-[600px] border-0"
                          title="Email Preview"
                          sandbox="allow-same-origin"
                        />
                      )}
                    </div>
                  </div>
                  
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    This is a preview of how the email will appear to recipients
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Review Modal */}
      <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {reviewAction === 'approve' ? 'Approve Document' : 'Reject Document'}
            </DialogTitle>
            <DialogDescription>
              {selectedDocument && (
                <>
                  Reviewing <strong>{selectedDocument.document?.name}</strong> for{' '}
                  <strong>{selectedDocument.advertiser_name}</strong>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {reviewAction === 'reject' && (
              <div>
                <label className="text-sm font-medium">Rejection Reason *</label>
                <Select value={rejectionReason} onValueChange={setRejectionReason}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select reason" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="document_illegible">Document not legible</SelectItem>
                    <SelectItem value="name_mismatch">Name doesn't match</SelectItem>
                    <SelectItem value="document_expired">Document has expired</SelectItem>
                    <SelectItem value="wrong_document_type">Wrong document type</SelectItem>
                    <SelectItem value="suspicious_document">Suspicious/fraudulent document</SelectItem>
                    <SelectItem value="incomplete_information">Incomplete information</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div>
              <label className="text-sm font-medium">Admin Notes (optional)</label>
              <Textarea
                placeholder="Add any notes about this decision..."
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReviewModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmitReview}
              disabled={submitting || (reviewAction === 'reject' && !rejectionReason)}
              className={reviewAction === 'approve' ? 'bg-green-600 hover:bg-green-700' : ''}
              variant={reviewAction === 'reject' ? 'destructive' : 'default'}
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : reviewAction === 'approve' ? (
                <CheckCircle className="h-4 w-4 mr-2" />
              ) : (
                <XCircle className="h-4 w-4 mr-2" />
              )}
              {reviewAction === 'approve' ? 'Approve' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDocumentVerification;
